import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  CtyShowToast,
  CustomTabs,
  ErrorBoundary,
  PageHeader,
  SimpleDeleteConfirmationModal,
  checkType,
  CtyButton,
  isSorAuthError,
} from '@circuitry-ai/doc-ui';
import { Icon } from '@iconify/react';
import { QueuesTab } from './work-queues/QueuesTab';
import { EntityTypesTab } from './work-queues/EntityTypesTab';
import { WorkQueueEditor } from './work-queues/WorkQueueEditor';
import type { WorkQueueDraft } from './work-queues/work-queue-editor-utils';
import {
  getSorErrorMessage,
  useWorkQueuesApi,
} from './work-queues/useWorkQueuesApi';
import type {
  WorkQueue,
  WorkQueueEntityType,
  WorkQueueTeamRole,
} from './work-queues/work-queues-types';

const PageHeaderWithErrorBoundary = ErrorBoundary(PageHeader);

type WorkQueuesTabKey = 'queues' | 'entity-types';

type WorkQueueEditorState =
  | { mode: 'create' }
  | { mode: 'edit'; queue: WorkQueue }
  | null;

type WorkQueuesLoadError = { message: string; needsConnect: boolean };

function WorkQueuesSettings() {
  const { t } = useTranslation();
  const [selectedTab, setSelectedTab] = useState<WorkQueuesTabKey>('queues');

  const {
    fetchQueues,
    fetchEntityTypes,
    fetchTeamRoles,
    fetchRoleMembers,
    fetchQueueMemberUserIds,
    saveQueue,
    setEntityTypeEnabled,
    deleteQueue,
    connectSor,
  } = useWorkQueuesApi();
  const [queues, setQueues] = useState<WorkQueue[]>([]);
  const [isLoadingQueues, setIsLoadingQueues] = useState(true);
  const [entityTypes, setEntityTypes] = useState<WorkQueueEntityType[]>([]);
  const [teamRoles, setTeamRoles] = useState<WorkQueueTeamRole[]>([]);
  const [isSavingQueue, setIsSavingQueue] = useState(false);
  const [loadError, setLoadError] = useState<WorkQueuesLoadError | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [isConnecting, setIsConnecting] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<WorkQueue | null>(null);
  const [isDeletingQueue, setIsDeletingQueue] = useState(false);
  const [editor, setEditor] = useState<WorkQueueEditorState>(null);

  useEffect(() => {
    const controller = new AbortController();
    const { signal } = controller;

    const failures: { label: string; error: unknown }[] = [];
    const load = async <T,>(
      request: Promise<T>,
      onLoaded: (value: T) => void,
      label: string
    ) => {
      try {
        onLoaded(await request);
      } catch (error) {
        if (!signal.aborted) {
          console.error(`Failed to load ${label}`, error);
          failures.push({ label, error });
        }
      }
    };

    setIsLoadingQueues(true);
    setLoadError(null);
    // load() catches its own errors, so nothing here can reject
    void Promise.all([
      load(fetchEntityTypes(signal), setEntityTypes, 'entity types'),
      load(fetchTeamRoles(signal), setTeamRoles, 'team roles'),
      load(fetchQueues(signal), setQueues, 'work queues'),
    ]).finally(() => {
      if (signal.aborted) {
        return;
      }
      setIsLoadingQueues(false);
      if (!failures.length) {
        return;
      }
      // All three share one SOR token, so an auth failure usually fails every request: report it once
      const authFailure = failures.find(({ error }) => isSorAuthError(error));
      setLoadError(
        authFailure
          ? {
              message: getSorErrorMessage(
                authFailure.error,
                'Could not connect to the System of Record.'
              ),
              needsConnect: true,
            }
          : {
              message: failures
                .map(
                  ({ label, error }) =>
                    `Failed to load ${label}: ${getSorErrorMessage(
                      error,
                      'unknown error'
                    )}`
                )
                .join(' · '),
              needsConnect: false,
            }
      );
    });

    return () => controller.abort();
  }, [fetchEntityTypes, fetchTeamRoles, fetchQueues, reloadKey]);

  const handleRetryLoad = useCallback(() => setReloadKey((key) => key + 1), []);

  // Runs from a click, so the Auth0 consent/login popup isn't blocked
  const handleConnect = useCallback(async () => {
    setIsConnecting(true);
    try {
      await connectSor();
      setReloadKey((key) => key + 1);
    } catch (error) {
      console.error('Failed to connect to SOR', error);
      setLoadError({
        message: getSorErrorMessage(
          error,
          'Could not connect to the System of Record.'
        ),
        needsConnect: true,
      });
    } finally {
      setIsConnecting(false);
    }
  }, [connectSor]);

  const queueCountByEntityType = useMemo(
    () =>
      queues.reduce<Record<string, number>>((counts, queue) => {
        counts[queue.entity_type] = (counts[queue.entity_type] ?? 0) + 1;
        return counts;
      }, {}),
    [queues]
  );

  // Saved as data.enabled on the type's queue_entities record; the switch flips back if that fails
  const handleToggleEntityType = useCallback(
    async (key: string, enabled: boolean) => {
      const entityType = entityTypes.find((type) => type.key === key);
      if (!entityType) {
        return;
      }
      const replace = (next: WorkQueueEntityType) =>
        setEntityTypes((current) =>
          current.map((type) =>
            type.record_id === next.record_id ? next : type
          )
        );
      replace({ ...entityType, is_enabled: enabled });
      try {
        replace(await setEntityTypeEnabled(entityType, enabled));
      } catch (error) {
        console.error('Failed to update entity type', error);
        replace(entityType);
        CtyShowToast(
          checkType.error,
          getSorErrorMessage(
            error,
            `Failed to ${enabled ? 'enable' : 'disable'} "${entityType.name}".`
          )
        );
      }
    },
    [entityTypes, setEntityTypeEnabled]
  );

  const handleCreateQueue = useCallback(() => {
    setEditor({ mode: 'create' });
  }, []);

  // Saved members are loaded first so the editor preselects them; if that fails the editor stays
  // closed, since saving with an empty selection would delete the queue's members
  const openQueueEditor = useCallback(
    async (queue: WorkQueue) => {
      try {
        const userIds = await fetchQueueMemberUserIds(queue.queue_id);
        setEditor({
          mode: 'edit',
          queue: {
            ...queue,
            members: userIds.map((userId) => ({
              user_id: userId,
              name: '',
              email: '',
            })),
          },
        });
      } catch (error) {
        console.error('Failed to load queue members', error);
        CtyShowToast(
          checkType.error,
          getSorErrorMessage(error, `Couldn't load members of "${queue.name}".`)
        );
      }
    },
    [fetchQueueMemberUserIds]
  );

  const handleEditQueue = useCallback(
    (queue: WorkQueue) => {
      void openQueueEditor(queue);
    },
    [openQueueEditor]
  );

  const handleCloseEditor = useCallback(() => {
    setEditor(null);
  }, []);

  const handleSaveQueue = useCallback(
    async (draft: WorkQueueDraft) => {
      if (!editor || isSavingQueue) {
        return;
      }
      const existing = editor.mode === 'edit' ? editor.queue : undefined;
      setIsSavingQueue(true);
      try {
        const {
          queue: saved,
          memberSyncError,
          claimActionError,
        } = await saveQueue({
          draft,
          existing,
          entityType: entityTypes.find(
            (entityType) => entityType.uuid === draft.entity_type
          ),
          existingQueueKeys: queues
            .map((queue) => queue.source_record?.data.queueKey)
            .filter((key): key is string => !!key),
        });
        setQueues((current) =>
          existing
            ? current.map((queue) =>
                queue.queue_id === saved.queue_id ? saved : queue
              )
            : [...current, saved]
        );
        CtyShowToast(
          checkType.success,
          `Queue "${saved.name}" ${existing ? 'saved' : 'created'}.`
        );
        if (memberSyncError) {
          CtyShowToast(
            checkType.warning,
            `Queue saved, but members could not be updated: ${memberSyncError}`
          );
        }
        if (claimActionError) {
          CtyShowToast(
            checkType.warning,
            `Queue saved, but "Let members assign items to themselves" could not be updated: ${claimActionError}`
          );
        }
        setSelectedTab('queues');
        setEditor(null);
      } catch (error) {
        console.error('Failed to save work queue', error);
        CtyShowToast(
          checkType.error,
          getSorErrorMessage(
            error,
            `Failed to ${existing ? 'save' : 'create'} queue.`
          )
        );
      } finally {
        setIsSavingQueue(false);
      }
    },
    [editor, isSavingQueue, saveQueue, entityTypes, queues]
  );

  const handleConfirmDelete = useCallback(async () => {
    if (!deleteTarget || isDeletingQueue) {
      return;
    }
    setIsDeletingQueue(true);
    try {
      await deleteQueue(deleteTarget);
      setQueues((current) =>
        current.filter((queue) => queue.queue_id !== deleteTarget.queue_id)
      );
      CtyShowToast(checkType.success, `Queue "${deleteTarget.name}" deleted.`);
      setDeleteTarget(null);
    } catch (error) {
      // Keep the dialog open so the user can retry
      console.error('Failed to delete work queue', error);
      CtyShowToast(
        checkType.error,
        getSorErrorMessage(
          error,
          `Failed to delete queue "${deleteTarget.name}".`
        )
      );
    } finally {
      setIsDeletingQueue(false);
    }
  }, [deleteTarget, isDeletingQueue, deleteQueue]);

  if (editor) {
    const editingId = editor.mode === 'edit' ? editor.queue.queue_id : null;
    return (
      <WorkQueueEditor
        key={editingId ?? 'new'}
        queue={editor.mode === 'edit' ? editor.queue : undefined}
        entityTypes={entityTypes}
        teamRoles={teamRoles}
        existingQueueNames={queues
          .filter((queue) => queue.queue_id !== editingId)
          .map((queue) => queue.name)}
        isSaving={isSavingQueue}
        loadRoleMembers={fetchRoleMembers}
        onCancel={handleCloseEditor}
        onSave={handleSaveQueue}
      />
    );
  }

  return (
    <div className="flex flex-col gap-5 h-full min-h-0">
      <PageHeaderWithErrorBoundary
        heading="Work Queues"
        description="Route inspections, claims, payments, work orders and cases to the right team"
        pageHeaderClass="!px-0 !gap-1 !pb-0"
      />

      <CustomTabs
        tabs={[
          { key: 'queues', title: t('Queues'), id: 'work-queues-queues-tab' },
          {
            key: 'entity-types',
            title: t('Entity Types'),
            id: 'work-queues-entity-types-tab',
          },
        ]}
        selectedKey={selectedTab}
        onTabChange={(key) => setSelectedTab(key as WorkQueuesTabKey)}
      />

      {loadError && (
        <div
          role="alert"
          className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-danger-200 bg-danger-50 px-4 py-3"
        >
          <span className="flex items-start gap-2 text-sm text-danger-700">
            <Icon
              icon="solar:danger-circle-linear"
              width={18}
              className="mt-0.5 shrink-0"
            />
            <span>
              {loadError.needsConnect
                ? 'Connect your account to the System of Record to load work queues.'
                : loadError.message}
              {loadError.needsConnect && (
                <span className="block text-xs text-danger-600">
                  {loadError.message}
                </span>
              )}
            </span>
          </span>
          <div className="flex gap-2">
            {loadError.needsConnect && (
              <CtyButton
                size="sm"
                color="primary"
                variant="solid"
                isLoading={isConnecting}
                onPress={handleConnect}
              >
                Connect
              </CtyButton>
            )}
            <CtyButton
              size="sm"
              variant="bordered"
              isDisabled={isConnecting}
              onPress={handleRetryLoad}
            >
              Retry
            </CtyButton>
          </div>
        </div>
      )}

      {selectedTab === 'queues' && (
        <QueuesTab
          queues={queues}
          isLoading={isLoadingQueues}
          entityTypes={entityTypes}
          onCreate={handleCreateQueue}
          onEdit={handleEditQueue}
          onDelete={setDeleteTarget}
        />
      )}
      {selectedTab === 'entity-types' && (
        <EntityTypesTab
          entityTypes={entityTypes}
          queueCountByEntityType={queueCountByEntityType}
          onToggleEnabled={(key, enabled) =>
            void handleToggleEntityType(key, enabled)
          }
        />
      )}

      <SimpleDeleteConfirmationModal
        isOpen={deleteTarget !== null}
        onOpenChange={(open) => {
          if (!open) {
            setDeleteTarget(null);
          }
        }}
        title="Delete queue?"
        body={
          deleteTarget ? (
            <p className="text-sm text-default-600">
              <span className="font-semibold text-foreground">
                {deleteTarget.name}
              </span>{' '}
              will stop routing new items. Items already assigned stay with
              their assignees.
            </p>
          ) : null
        }
        requireTypedConfirmation={false}
        confirmLabel="Delete"
        cancelLabel="Cancel"
        confirmColor="danger"
        onConfirm={handleConfirmDelete}
        isConfirmLoading={isDeletingQueue}
      />
    </div>
  );
}

export default WorkQueuesSettings;
